import mixpanel from "mixpanel-browser";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BeatLoader } from "react-spinners";
import styled, { useTheme } from "styled-components";
import { routeList } from "../App";
import { lang } from "../assets/languages/en.language";
import Popup from "../components/Popup.component";
import AppLogo from "../components/svgs/AppLogo";
import { ClockIcon } from "../components/svgs/icons/Clock.icon";
import { CameraModal, CameraType } from "../modals/Camera.modal";
import api from "../services/api";
import { useUserStore } from "../store/useUserStore";
import { RoundCta, TertiaryFitContentButton } from "../styles/Button.style";
import { MainContainer, RootContainer } from "../styles/Container.style";
import { Paragraph, Title } from "../styles/Text";
import { Column, Gap } from "../styles/Utils";

export default function VerifyAccountPage() {
  const [sumbmitting, setSumbmitting] = useState<boolean>(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const [selfie, setSelfie] = useState<string | null>(null);
  const [idPicture, setIdPicture] = useState<string | null>(null);
  const [isCameraSelfieOpen, setIsCameraSelfieOpen] = useState<boolean>(false);
  const [isCameraIdOpen, setIsCameraIdOpen] = useState<boolean>(false);
  const [submitedPopupVisible, setSubmitedPopupVisible] = useState<boolean>(false);
  const {
    profile: { accountVerified, matchs, accountVerificationSubmitted },
    setAccountVerificationSubmitted,
    addMatch,
  } = useUserStore();

  // Check by-pass
  useEffect(() => {
    if (accountVerificationSubmitted || (!accountVerificationSubmitted && accountVerified)) navigate(routeList.swiper);
  }, []);

  const handleSkip = async () => {
    if (!matchs.includes(import.meta.env.VITE_CYNO_STAFF_PROFILE_ID)) {
      await api.skipAccountVerification();
      // Necessary to prevent StaffMatchHappening Page two times.
      addMatch(import.meta.env.VITE_CYNO_STAFF_PROFILE_ID);
      navigate(routeList.staffMatchHappening);
    } else navigate(routeList.swiper);
  };

  const handleSelfieShot = () => {
    setIsCameraSelfieOpen(true);
    // mixpanel.track("Open Selfie Camera");
  };

  const handleIdShot = () => {
    setIsCameraIdOpen(true);
    // mixpanel.track("Open Camera For ID");
  };

  const submit = async (selfie: string, idPicture: string) => {
    setSumbmitting(true);
    try {
      await api.veryfyAccount({ selfie, idPicture });
      mixpanel.track("Account Verify Submited");
      setAccountVerificationSubmitted(true);
      setSubmitedPopupVisible(true);
    } catch (err) {
      console.log("Error adding identity pictures", err);
      alert("Error adding identity pictures");
    }
    setSumbmitting(false);
  };

  useEffect(() => {
    if (selfie && idPicture) submit(selfie, idPicture);
  }, [selfie, idPicture]);

  const handlePopupClosing = () => {
    if (!matchs.includes(import.meta.env.VITE_CYNO_STAFF_PROFILE_ID)) {
      navigate(routeList.staffMatchHappening);
    } else navigate(routeList.swiper);
  };

  return (
    <>
      <RootContainer $background={theme.color.primary} $height="100dvh" $topSafe={true}>
        <Column $alignItem="center" $maxHeight>
          <AppLogoWrapper>
            <AppLogo color={theme.color.tertiaryLight} />
          </AppLogoWrapper>
          <MainContainer $padding="3rem 1rem">
            <Title>{lang.verifyAccountPage.title}</Title>
            <Gap $height={1} />
            <Paragraph>{lang.verifyAccountPage.message}</Paragraph>
            <Gap $height={2} />
            <RoundCta
              onClick={handleIdShot}
              $color={theme.color.primaryDark}
              $background={theme.color.tertiary}
              disabled={Boolean(idPicture)}
            >
              {lang.verifyAccountPage.buttonText1}
            </RoundCta>
            <Gap $height={2} />
            <RoundCta
              onClick={handleSelfieShot}
              $color={theme.color.tertiaryLight}
              $background={theme.color.primary}
              disabled={Boolean(selfie)}
            >
              {lang.verifyAccountPage.buttonText2}
            </RoundCta>
            <IllustrationWrapper>
              <img src="/illustrations/girl-dog-on-carpet.png" />
            </IllustrationWrapper>
          </MainContainer>
          <Gap $height={1.5} />
          <TertiaryFitContentButton onClick={handleSkip}>
            {sumbmitting ? (
              <BeatLoader color={theme.color.tertiaryLight} />
            ) : (
              <span>{lang.verifyAccountPage.buttons.skip}</span>
            )}
          </TertiaryFitContentButton>
        </Column>
      </RootContainer>
      {isCameraSelfieOpen && (
        <CameraModal
          onClose={(capturedImage) => {
            if (capturedImage) {
              setSelfie(capturedImage);
            }
            setIsCameraSelfieOpen(false);
          }}
          preferredFacingMode={CameraType.FRONT}
        />
      )}
      {isCameraIdOpen && (
        <CameraModal
          onClose={(capturedImage) => {
            if (capturedImage) {
              setIdPicture(capturedImage);
            }
            setIsCameraIdOpen(false);
          }}
          preferredFacingMode={CameraType.BACK}
        />
      )}
      {submitedPopupVisible && (
        <Popup
          type="info"
          title={lang.verifyAccountPage.submited_popup.title}
          message={lang.verifyAccountPage.submited_popup.message}
          buttonText={lang.verifyAccountPage.submited_popup.buttonText}
          Icon={ClockIcon}
          onClose={() => handlePopupClosing()}
          action={() => handlePopupClosing()}
        />
      )}
    </>
  );
}

const IllustrationWrapper = styled.div`
  z-index: -1;
  width: 100%;
  position: relative;
  height: 100%;
  img {
    object-fit: contain !important;
    width: 100%;
    position: absolute;
    bottom: -3.5rem;
    object-position: bottom;
  }
`;

// const IllustrationWrapper = styled.div`
//   width: 100%;
//   position: relative;
//   height: 100%;
//   img {
//     object-fit: contain !important;
//     width: 100%;
//     position: absolute;
//     bottom: -3.5rem;
//     height: 120%;
//     object-position: bottom;
//   }
// `;

export const AppLogoWrapper = styled.div`
  z-index: 1;
  height: 7rem;
  width: 70%;
  svg {
    width: 100%;
  }
`;
